import { Navigate, Outlet } from 'react-router-dom';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useWorkspaceKind, isFirmWorkspace } from '../workspace/useWorkspaceKind';
import { useAdvisorFirm } from './useAdvisorFirm';

/**
 * Route guard for pages that only make sense in a FAMILY workspace: originally just the
 * Advisors page (steward-app#798; Settings > Advisors, moved to People > Advisors by
 * steward-app#1922) — "Connect your family to an advisory firm" has no meaning from
 * inside the firm's own workspace, since following it there would issue advisor
 * invitations, and create a "Shared with «firm»" folder, in the firm's own workspace
 * rather than a client's — and since steward-app#1541/#808, two more
 * family-only surfaces reachable by a direct URL: Settings > Connections (a firm has no
 * linked banks to aggregate) and `/ask` (the assistant's prompt/setup is not firm-aware
 * yet, a deliberate deferral per #1541). Since steward-app#1730, Settings > Workspace as
 * well: a firm renames itself on the Firm page, and has no Delete.
 *
 * **`to` defaults to `/settings`**, right for every Settings-tab consumer (Connections,
 * Workspace) and matching `RequireAdmin`'s own denial pattern. People > Advisors keeps the
 * default too: it is where a refused `/settings/advisors` landed before the page moved
 * (steward-app#1922 changed where the page lives, not where a refusal goes). `/ask` has no Settings
 * tab to fall back to, so its route wraps this with `to="/"` instead — landing on the
 * ordinary index route, which `HomeRoute` already sends a firm workspace on to
 * `/advisor` or `FirmWorkspaceNotReady`, exactly as it would have if the caller had
 * never typed `/ask` at all.
 *
 * Mirrors `RequireAdmin`: render nothing while membership is still resolving, redirect
 * to `to` otherwise.
 *
 * 🚨 **`inFirmWorkspace` calls the shared `isFirmWorkspace` helper
 * (`workspace/useWorkspaceKind.ts`), not `workspaceKind === 'firm'` alone and not `advisorFor`
 * directly (steward-app#1307, extracted into the shared helper by steward-app#736,
 * 2026-09-23).** This read `!!advisorFor && (workspaceKind === 'firm' || ...)` until #1307,
 * byte-identical to `Settings.tsx`'s copy, and the leading `&&` gated the WHOLE expression:
 * with `advisorFor` null the predicate was `false` however `workspaceKind` answered, so a
 * firm's own staff standing in their own firm workspace — during the window where their `GET
 * /advisor/firms` has not resolved — were let through to the page this guard exists to keep
 * them off. Both layers failed together and at the same moment, which is why hiding the tab
 * did not cover for it. `Layout.tsx`'s `SidebarContent` dropped the same kind of leading gate
 * in #1271 for the same reason (its gate was `advisorNavVisible &&`, i.e. `advisorFor` OR
 * `firmChoice`, so strictly weaker than this one — this file has never read `firmChoice`).
 * "Is this workspace a firm's" is a fact about the WORKSPACE and does not wait on this
 * caller's own advisor relationship resolving.
 *
 * ⚠️ **The `!activeMembership` early return two lines below rules out only the null/undefined
 * HALF of the shared helper's stand-in at this site — not the other half.** By the time
 * `isFirmWorkspace` runs here, `activeMembership` is always truthy, so the id-comparison arm
 * can no longer fire via an UNRESOLVED membership. It can still fire via the OTHER trigger a
 * KNOWN membership carries: a degraded packs read (`activeMembership.packsResolved === false`,
 * set by `services/steward-app-api/src/handlers/me.ts`'s catch branch on a `getWorkspace`
 * failure). That is reachable here, and is a correction, not a dead arm found and left in:
 * without it, a firm admin whose packs read failed transiently would read as `workspaceKind
 * === 'family'` (the degraded `packs: []`) and this guard would ADMIT them — exactly the #798
 * failure it exists to prevent, on a path this file's own early return does nothing to close.
 * `RequireFamilyWorkspace.test.tsx` pins this case; do not read "dead at THIS site" as true of
 * the whole stand-in, only of its null/undefined trigger.
 *
 * `Settings.tsx`, `PeopleLayout.tsx`, `useClientFrame.ts` and `HomeRoute.tsx` have no
 * `!activeMembership` gate of their own, so both triggers reach the stand-in at those sites;
 * calling the shared helper here too, rather than the reduced `workspaceKind === 'firm'` this
 * file could get away with for the null/undefined case alone, is what keeps the call sites from drifting
 * apart the way #1307 found two of them had — and is what catches the packsResolved case here
 * as well.
 *
 * **`PeopleLayout.tsx` computes this same predicate from the same inputs for the Advisors
 * tab, and the two move together** (it was `Settings.tsx` until steward-app#1922 moved the
 * tab and this route to People together). They drifted into the #1307 hole together and must
 * not drift apart again — the shared helper is what makes that structural rather than a
 * discipline to remember. `PeopleLayout.tsx` also carries the note on what keying on
 * `workspaceKind` first costs in the OTHER direction (a family workspace misread as a firm's
 * loses the tab there AND is redirected here); that argument applies to both files and is
 * written down once, there. `Settings.tsx` still calls the same helper for Connections and
 * Workspace.
 *
 * Belt and braces with the tab itself being hidden (`PeopleLayout.tsx`): this exists for a
 * bookmarked or hand-typed `/people/advisors` URL (or an old `/settings/advisors` one, which
 * redirects there), where the primary defense (an absent tab) doesn't apply. Like every other guard in this router, this is UX gating,
 * and appcore's membership/capability checks are the only thing that can actually refuse
 * a request.
 *
 * ⚠️ **Do not read that as a safety net behind this gate, and note that it covers ONE of
 * the two cases, not both** (raised in this branch's security review, 2026-09-08, and
 * checked at source here rather than taken on the review's word). This paragraph is about
 * the ADVISORS consumer specifically — Connections and `/ask` have no write of their own
 * whose target could be wrong (#808's "What this is NOT"; `/ask` is a read/prompt surface,
 * not a write to another workspace), so neither case below applies to them:
 *
 *  - Connecting **a different** registered firm from inside this firm's own workspace: the
 *    caller genuinely IS an admin of this workspace, so the writes are legitimate
 *    operations aimed at the wrong workspace and nothing downstream has a basis to refuse
 *    them. This gate and the tab are the only things that know the target is wrong.
 *  - Connecting **this same** firm to itself: refused, at engagement create. That is a
 *    cross-repo claim, not something this repo can prove — the façade states it in
 *    `handlers/advisor.ts`'s roster filter ("appcore refuses an engagement naming a
 *    workspace inside the firm's own tenant", steward-app#1021), and appcore is where it
 *    would actually have to hold. Do not widen it into "appcore refuses these writes".
 */
export function RequireFamilyWorkspace({ to = '/settings' }: { to?: string }) {
  const { activeWorkspaceId, activeMembership } = useWorkspace();
  const { advisorFor } = useAdvisorFirm();
  const workspaceKind = useWorkspaceKind();
  if (!activeMembership) return null;
  const inFirmWorkspace = isFirmWorkspace(workspaceKind, activeMembership, activeWorkspaceId, advisorFor);
  if (inFirmWorkspace) return <Navigate to={to} replace />;
  return <Outlet />;
}
